<template>
  <div class="tile-scroller-container buttons-top">
    <div ref="tileScrollerWrapper" class="tile-scroller-wrapper">
      <div ref="tileScrollerElement" class="tile-scroller" :style="{ transition: 'all 0.5s ease', transform: `translate(${translate})` }" @transitionend="handleTransitionEnd">
        <div class="related-news-tile" v-for="(data, index) in itemList" :key="index">
          <a :href="data.Url">
            <div class="bordered-item">
              <div class="bordered-item-wrapper">
                <div class="related-news-tile-wrapper">
                  <div class="tile-image" v-if="data.TileImageUrl">
                    <img :src="data.TileImageUrl" :alt="data.TileImageAltText" />
                  </div>
                  <div class="related-news-tile-content-wrapper">
                    <h4 v-html="data.TileTitle"></h4>
                    <div class="related-news-tile-content" v-html="data.TileContent"></div>
                    <div class="related-news-tile-date"><span>{{ diffDates(data.TileDate) }}</span> day(s) ago</div>
                  </div>
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
      <div class="tile-scroller-controls">
        <button class="slider-prev-button" @click="prev" :disabled="isFirstNewsTileVisible"></button>
        <button class="slider-next-button" @click="next" :disabled="isLastNewsTileVisible"></button>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, nextTick, onMounted, ref } from 'vue';
  import { useResizeObserver } from '@vueuse/core';
  import { first, last } from 'lodash';

  interface NewsArticleTileItemModel {
    TileImageUrl?: string;
    TileImageAltText?: string;
    TileTitle: string;
    TileContent?: string;
    Url: string;
    TileDate: Date;
  }

  const props = defineProps({
    items: {
      type: String,
      required: true,
    },
  });

  const tileScrollerElement = ref<HTMLDivElement>(null!);
  const tileScrollerWrapper = ref<HTMLDivElement>(null!);
  const firstNewsTile = ref<HTMLDivElement>(null!);
  const lastNewsTile = ref<HTMLDivElement>(null!);
  const isFirstNewsTileVisible = ref<boolean>(true);
  const isLastNewsTileVisible = ref<boolean>(false);

  const tileIndex = ref(0);
  const tileWidth = ref(0);
  const tileWidthMargin = ref(20); // add value pixels to scroll extra, eg margin between tiles
  const translate = computed(() => (tileIndex.value === 0 ? '0' : `${(tileWidth.value + tileWidthMargin.value) * -tileIndex.value}px`));
  const isTranslating = ref<boolean>(false);

  const itemList = ref<NewsArticleTileItemModel[]>([]);

  function diffDates(tileDate: Date) {
    var actualDate = new Date(tileDate);
    // Tile date is a date only in umbraco, so set the default time offset back to midnight
    actualDate.setHours(0, 0, 0, 0);

    var today = new Date();
    var mms = today.getTime() - actualDate.getTime();
    return Math.ceil(mms / (1000 * 60 * 60 * 24));
  }

  function prev() {
    if (!isTranslating.value) {
      tileIndex.value = tileIndex.value - 1;
      isTranslating.value = true;
    }
  }
  function next() {
    if (!isTranslating.value) {
      tileIndex.value = tileIndex.value + 1;
      isTranslating.value = true;
    }
  }

  useResizeObserver(tileScrollerElement, () => {
    const newsArticleTile = tileScrollerElement.value.querySelector('.related-news-tile') as HTMLDivElement;
    tileWidth.value = newsArticleTile?.offsetWidth ?? 0;
  });

  function handleTransitionEnd(e: TransitionEvent) {
    if (e.propertyName === 'transform' && isTranslating.value) {
      if (firstNewsTile.value) {
        const rect = firstNewsTile.value.getBoundingClientRect();
        const startRect = tileScrollerWrapper.value.getBoundingClientRect().left;
        isFirstNewsTileVisible.value = rect.left >= startRect;
      }
      if (lastNewsTile.value) {
        const rect = lastNewsTile.value.getBoundingClientRect();
        const endRect = tileScrollerWrapper.value.getBoundingClientRect().right;
        isLastNewsTileVisible.value = rect.right <= endRect;
      }
      isTranslating.value = false;
    }
  }

  onMounted(async () => {
    itemList.value = JSON.parse(props.items);

    await nextTick();
    const newsTiles = tileScrollerElement.value.querySelectorAll('.related-news-tile');

    firstNewsTile.value = first(newsTiles) as HTMLDivElement;
    lastNewsTile.value = last(newsTiles) as HTMLDivElement;
  });
</script>
